import React, {useMemo} from 'react';
import {List} from 'immutable';
import {GUIDE_TYPES} from 'client/constants';
import type {SubjectModelV2, QuestionModelV3, QuestionSetModelV1} from '@albert-io/models';
import {useShallow} from 'zustand/react/shallow';

import AnnotatableContent from 'client/components/PracticeView/Annotations/AnnotatableContent/AnnotatableContent';
import type {QuestionRegionKind} from 'client/components/PracticeView/Annotations/shared/annotationRegions';
import {buildQuestionRegionId} from 'client/components/PracticeView/Annotations/shared/annotationRegions';

import {extractQuestionStem, stripQuestionStem} from '../questionStem';

import MarkdownBlock from '../MarkdownBlock/MarkdownBlock';
import {
  useQuestionTranslationStore,
  ExplanationText,
  SampleResponse as SampleResponseText
} from '../TranslationSection';
import {ALTERNATE_CONTENT_LANGUAGES} from '../TranslationSection/Translation.types';
import {TagsList, StandardsList} from '../TagsStandardsList/TagsStandardsList.react';

import './content-blocks.scss';

export type PromptVariant = 'full' | 'promptWithoutStem' | 'stem';

// 'full' and 'promptWithoutStem' may share the prompt region — they never co-mount.
const PROMPT_VARIANTS: Record<
  PromptVariant,
  {regionKind: QuestionRegionKind; transformText?: (text: string) => string}
> = {
  full: {regionKind: 'prompt'},
  promptWithoutStem: {regionKind: 'prompt', transformText: stripQuestionStem},
  stem: {regionKind: 'stem', transformText: extractQuestionStem}
};

/** Structural rather than `QuestionModelV3`, which is untyped JS, so the nullable prompt is checked. */
interface QuestionPromptSource {
  getId(): string;
  getPrompt(): string | null;
}

interface PromptProps {
  question?: QuestionPromptSource;
  /** Also names this mount's annotation region. */
  variant: PromptVariant;
}

export const Prompt = ({question, variant}: PromptProps) => {
  const {regionKind, transformText} = PROMPT_VARIANTS[variant];
  const {translatedQuestionInfo, showPromptTranslation} = useQuestionTranslationStore(
    useShallow((state) => ({
      translatedQuestionInfo: state.translatedQuestionInfo,
      showPromptTranslation: state.showTranslations && state.translationSettings.question
    }))
  );

  const translated = showPromptTranslation ? translatedQuestionInfo : null;

  const text = useMemo(() => {
    const promptText = (translated ? translated.prompt : question?.getPrompt()) ?? '';
    return transformText ? transformText(promptText) : promptText;
  }, [question, translated, transformText]);

  const regionId = question ? buildQuestionRegionId(question.getId(), regionKind) : null;
  // The translation payload doesn't name its language; the store always
  // fetches ALTERNATE_CONTENT_LANGUAGES[0] (TranslationStore.fetchTranslation).
  const lang = translated ? ALTERNATE_CONTENT_LANGUAGES[0] : null;

  return (
    <AnnotatableContent regionId={regionId} lang={lang}>
      <MarkdownBlock key={question?.getId()} text={text} />
    </AnnotatableContent>
  );
};

interface ExplainationProps {
  question?: QuestionModelV3;
}

export const Explanation = ({question}: ExplainationProps) => {
  const {translatedQuestionInfo, showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      translatedQuestionInfo: state.translatedQuestionInfo,
      showTranslations: state.showTranslations && state.translationSettings.question
    }))
  );

  const text = useMemo(() => {
    if (showTranslations && translatedQuestionInfo) {
      return translatedQuestionInfo.solution_text;
    }
    return question.getMeta().getSolutionText();
  }, [question, translatedQuestionInfo, showTranslations]);

  const headingText = useMemo(
    () => (showTranslations ? ExplanationText : 'Explanation'),
    [showTranslations]
  );

  return <MarkdownBlock heading={headingText} text={text} />;
};

interface SampleResponseProps {
  question?: QuestionModelV3;
}

export const SampleResponse = ({question}: SampleResponseProps) => {
  const {translatedQuestionInfo, showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      translatedQuestionInfo: state.translatedQuestionInfo,
      showTranslations: state.showTranslations && state.translationSettings.question
    }))
  );
  const sample: string = useMemo(() => {
    if (showTranslations) {
      return translatedQuestionInfo?.sample;
    }
    return question.getMeta().getSample();
  }, [question, showTranslations, translatedQuestionInfo]);

  return (
    <>
      {sample && (
        <MarkdownBlock
          heading={showTranslations ? SampleResponseText : 'Sample Response'}
          text={sample}
        />
      )}
    </>
  );
};

interface StandardsProps {
  question?: QuestionModelV3;
  subject?: SubjectModelV2;
  questionSet?: QuestionSetModelV1;
}

export const Standards = ({question, subject, questionSet}: StandardsProps) => {
  const guideTypeLinkString = useMemo(() => {
    const guideType = questionSet.getGuideLevels().first()?.getGuide()?.getGuideType();
    const typeLinkString = guideType === GUIDE_TYPES.ASSESSMENT ? '/assessments' : '';
    return typeLinkString;
  }, [questionSet]);

  return (
    <StandardsList
      standards={question.getStandards()}
      linkToFunc={(item) =>
        `/${subject.getUrlSlug()}${guideTypeLinkString}?standards=${item.getId()}`
      }
    />
  );
};

interface TagsProps {
  question?: QuestionModelV3;
  subject?: SubjectModelV2;
  questionSet?: QuestionSetModelV1;
}

export const Tags = ({question, subject, questionSet}: TagsProps) => {
  const guideTypeLinkString = useMemo(() => {
    const guideType = questionSet.getGuideLevels().first()?.getGuide()?.getGuideType();
    const typeLinkString = guideType === GUIDE_TYPES.ASSESSMENT ? '/assessments' : '';
    return typeLinkString;
  }, [questionSet]);
  return (
    <TagsList
      tags={question.getQuestionSet().getTags ? question.getQuestionSet().getTags() : List()}
      linkToFunc={(item) => `/${subject.getUrlSlug()}${guideTypeLinkString}?tags=${item.getId()}`}
    />
  );
};
